import { Check, Copy, ExternalLink, FileText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DetailedPoliceReport,
  OfficerDetail,
} from '@/services/api/generated/policeReportsService';
import { usePoliceReportsListQuery } from '@/services/api/policeReportsService';

import { MatterSectionCard } from './MatterSectionCard';

interface PoliceReportInfoCardProps {
  matterId: string;
}

interface CopyableValueProps {
  label: string;
  value: string;
}

const CopyableValue = ({ label, value }: CopyableValueProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium text-zinc-900',
        'border-b border-dashed border-zinc-300 hover:border-zinc-500',
        'cursor-pointer transition-colors',
      )}
      title={`Click to copy ${label}`}
    >
      <span>{value}</span>
      {copied ? (
        <Check className="h-3 w-3 text-emerald-500" />
      ) : (
        <Copy className="h-3 w-3 text-zinc-400" />
      )}
    </button>
  );
};

interface InfoRowProps {
  label: string;
  value: string | null | undefined;
  copyable?: boolean;
  className?: string;
}

const InfoRow = ({
  label,
  value,
  copyable = false,
  className,
}: InfoRowProps) => {
  if (!value) return null;

  return (
    <div className={cn('flex items-start gap-6', className)}>
      <span className="text-xs text-zinc-500 min-w-[120px]">{label}</span>
      {copyable ? (
        <CopyableValue label={label} value={value} />
      ) : (
        <span className="text-xs font-medium text-zinc-900">{value}</span>
      )}
    </div>
  );
};

const formatDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  try {
    // Parse date parts directly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    if (year && month && day) {
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const formatOfficer = (
  officers: OfficerDetail[] | null | undefined,
): string | null => {
  if (!officers || officers.length === 0) return null;
  const officer = officers[0];
  let formatted = officer.name || 'Unknown Officer';
  if (officer.badge_number) {
    formatted += `, Badge #${officer.badge_number}`;
  }
  return formatted;
};

// Format attestation status for display
const formatAttestationStatus = (
  status: string | null | undefined,
): { label: string; colorClass: string } => {
  switch (status) {
    case 'verified_received':
      return {
        label: 'Verified',
        colorClass: 'bg-emerald-100 text-emerald-700',
      };
    case 'attested_verified':
      return {
        label: 'Confirmed',
        colorClass: 'bg-emerald-100 text-emerald-700',
      };
    case 'attested_unverified':
      return { label: 'Unverified', colorClass: 'bg-amber-100 text-amber-700' };
    case 'unclear':
      return { label: 'Unclear', colorClass: 'bg-zinc-100 text-zinc-600' };
    case 'inferred':
      return { label: 'Inferred', colorClass: 'bg-blue-100 text-blue-700' };
    case 'rejected_verified':
      return { label: 'Rejected', colorClass: 'bg-red-100 text-red-700' };
    default:
      return { label: 'Unknown', colorClass: 'bg-zinc-100 text-zinc-600' };
  }
};

// Extract citations/violations from suggestions or description
const extractCitationsIssued = (
  report: DetailedPoliceReport,
): string | null => {
  // Check suggestions for citation-related info in the body
  if (report.suggestions && report.suggestions.length > 0) {
    const citationSuggestion = report.suggestions.find(
      (s) =>
        s.body &&
        typeof s.body === 'string' &&
        s.body.toLowerCase().includes('cited'),
    );
    if (
      citationSuggestion?.body &&
      typeof citationSuggestion.body === 'string'
    ) {
      return citationSuggestion.body;
    }
  }

  // Check description for citation mentions
  if (report.description) {
    const citationMatch = report.description.match(
      /(?:cited|citation|violation)[^.]*(?:\([^)]+\))?/i,
    );
    if (citationMatch) {
      return citationMatch[0].trim();
    }
  }

  return null;
};

interface SingleReportDisplayProps {
  report: DetailedPoliceReport;
}

const SingleReportDisplay = ({ report }: SingleReportDisplayProps) => {
  const departmentName = report.police_department?.name || 'Unknown Department';
  const reportNumber = report.report_identifier;
  const reportDate = formatDate(report.reported_incident_date);
  const officer = formatOfficer(report.involved_officers);
  const citationsIssued = extractCitationsIssued(report);

  return (
    <>
      {/* Two-column layout for main info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
        <InfoRow
          label="Report Number"
          value={reportNumber}
          copyable={!!reportNumber}
        />
        <InfoRow label="Report Date" value={reportDate} />
        <InfoRow label="Department" value={departmentName} />
        {officer && <InfoRow label="Responding Officer" value={officer} />}
      </div>

      {/* Citations Issued - Full width if present */}
      {citationsIssued && (
        <div className="pt-3 mt-3 border-t border-zinc-100">
          <div className="flex items-start gap-6">
            <span className="text-xs text-zinc-500 min-w-[120px]">
              Citations Issued
            </span>
            <span className="text-xs font-medium text-zinc-900 text-right flex-1">
              {citationsIssued}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export const PoliceReportInfoCard = ({
  matterId,
}: PoliceReportInfoCardProps) => {
  const { data: policeReports, isLoading } = usePoliceReportsListQuery({
    matterId,
  });

  // Get the primary/most relevant police report
  // Prefer verified_received > attested_verified > attested_unverified > others
  const primaryReport = policeReports?.reduce<DetailedPoliceReport | null>(
    (best, current) => {
      if (!best) return current;

      const statusPriority: Record<string, number> = {
        verified_received: 5,
        attested_verified: 4,
        attested_unverified: 3,
        unclear: 2,
        inferred: 1,
        rejected_verified: 0,
      };

      const bestPriority = statusPriority[best.attestation_status || ''] ?? 1;
      const currentPriority =
        statusPriority[current.attestation_status || ''] ?? 1;

      return currentPriority > bestPriority ? current : best;
    },
    null,
  );

  if (isLoading) {
    return (
      <MatterSectionCard
        title="Police"
        icon={<FileText className="h-4 w-4" />}
        isLoading
      >
        <div />
      </MatterSectionCard>
    );
  }

  if (!primaryReport) {
    return (
      <MatterSectionCard
        title="Police"
        icon={<FileText className="h-4 w-4" />}
        bodyClassName="px-4 py-2"
      >
        <p className="text-sm text-zinc-500">No police report on file</p>
      </MatterSectionCard>
    );
  }

  const status = formatAttestationStatus(primaryReport.attestation_status);

  return (
    <MatterSectionCard
      title="Police"
      icon={<FileText className="h-4 w-4" />}
      bodyClassName="px-4 py-2"
      headerActions={
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'px-1.5 py-0.5 rounded text-[10px] font-medium',
              status.colorClass,
            )}
          >
            {status.label}
          </span>
          <Link
            to={`/matters/${matterId}/police/${primaryReport.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px] text-zinc-500 hover:text-zinc-700 gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              View Report
            </Button>
          </Link>
        </div>
      }
    >
      <SingleReportDisplay report={primaryReport} />
    </MatterSectionCard>
  );
};
